/**
 * @module NotificationItem
 * @package @geenius/notifications-solidjs-css
 * @description Renders a single inbox row for the SolidJS vanilla CSS
 * variant.
 */

import type { Notification } from '@geenius/notifications/shared'
import {
  formatNotificationRelativeTime,
  getNotificationIcon,
} from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { Show } from 'solid-js'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for a single notification row.
 */
export interface NotificationItemProps {
  notification: Notification
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  class?: string
}

/**
 * Renders a single notification row with optional read and dismiss actions.
 *
 * @param props Notification row props.
 * @returns The rendered notification row.
 */
export function NotificationItem(props: NotificationItemProps): JSX.Element {
  const { t } = createNotificationCopy()
  const isUnread = () => props.notification.status === 'unread'

  return (
    <article
      class={cx(
        'notifications__item',
        'gm-motion',
        'gm-motion--fade-in',
        isUnread() ? 'notifications__item--unread' : 'notifications__item--read',
        props.class,
      )}
    >
      <Show
        when={props.notification.actorAvatar}
        fallback={
          <span
            class={cx(
              'notifications__item-icon',
              `notifications__item-icon--${props.notification.type}`,
            )}
          >
            {getNotificationIcon(props.notification.type)}
          </span>
        }
      >
        <img class={cx('notifications__item-avatar')} src={props.notification.actorAvatar} alt="" />
      </Show>
      <div class={cx('notifications__item-content')}>
        <button
          type="button"
          class={cx('notifications__item-trigger')}
          onClick={() => {
            if (isUnread()) {
              props.onRead?.(props.notification.id)
            }
          }}
          disabled={!isUnread() || !props.onRead}
          aria-label={
            isUnread()
              ? t('notifications.item.markReadAria', {
                  title: props.notification.title,
                })
              : t('notifications.item.notificationAria', {
                  title: props.notification.title,
                })
          }
        >
          <div class={cx('notifications__item-header')}>
            <div>
              <div class={cx('notifications__item-title')}>{props.notification.title}</div>
              <div class={cx('notifications__item-body')}>{props.notification.body}</div>
            </div>
            <Show when={isUnread()}>
              <span class={cx('notifications__item-unread-dot')} />
            </Show>
          </div>
        </button>
        <div class={cx('notifications__item-footer')}>
          <span class={cx('notifications__item-time')}>
            {formatNotificationRelativeTime(props.notification.createdAt, t)}
          </span>
          <Show when={props.notification.actionUrl}>
            <a class={cx('notifications__item-action')} href={props.notification.actionUrl}>
              {props.notification.actionLabel ?? t('notifications.common.view')}
            </a>
          </Show>
          <Show when={props.onDismiss}>
            <button
              type="button"
              class={cx('notifications__item-dismiss')}
              onClick={() => props.onDismiss?.(props.notification.id)}
            >
              {t('notifications.common.dismiss')}
            </button>
          </Show>
        </div>
      </div>
    </article>
  )
}
